"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useAppSelector } from "@/store/store";
import { creditApi, useGetCreditBalanceQuery } from "@/store/api/creditApi";
import { pricingApi } from "@/store/api/pricingApi";
import { providersApi } from "@/store/api/providersApi";
import { teamApi } from "@/store/api/teamApi";
import {
  getOperationPriceSources,
  resolveOperationCost,
  resolveOperationSpend,
} from "@/lib/utils/credit-guard.utils";
import { formatCredits } from "@/lib/utils/credit.utils";
import type { Provider, ProviderCategory } from "@/lib/types/provider.types";
import type { BillingSettings, ServiceCreditPrice } from "@/store/api/pricingApi";
import type {
  CreditCheckOptions,
  CreditCheckResult,
  CreditGuardContextValue,
  CreditOperation,
  InsufficientCreditsContext,
} from "@/lib/types/credit-guard.types";
import { CreditGuardModal } from "./CreditGuardModal";
import { BuyCreditsDialog } from "@/app/(features)/settings/components/buy-credits-dialog";

const CreditGuardContext = createContext<CreditGuardContextValue | undefined>(
  undefined
);

interface PriceData {
  providersByCategory: Partial<Record<ProviderCategory, Provider[] | undefined>>;
  serviceConfigs: ServiceCreditPrice[] | undefined;
}

/**
 * Credit guard — one place that decides whether an action can be afforded, and
 * the modal that explains it when it can't.
 *
 * Mounted once inside the authenticated shell (client-layout.tsx). Every page
 * under it can call `useCreditGuard()`.
 *
 * ## What it costs to have mounted
 *
 * Nothing extra on page load. The only query it subscribes to is the balance,
 * which the top-bar badge already fetches — same endpoint, same cache entry,
 * one request. Prices (the provider catalogue and the pricing bundle) and team
 * membership are fetched LAZILY, on the first guarded click, and served from
 * the RTK Query cache after that. Prices are static enough that a cached read
 * is the right answer; the balance is not, so `checkCredits` always refetches
 * it before deciding — this is money, and a stale badge value is exactly the
 * kind of thing that lets a spend through on an empty account.
 *
 * ## Why "minimum to start" and not "cost × count"
 *
 * An unlock here is a provider waterfall billed per attempt, so the real cost
 * of one unlock is not knowable in advance and the cost of 25 is even less so.
 * Requiring 25 credits before letting someone unlock 25 contacts would block a
 * run that would legitimately have unlocked the first 8 and stopped. So the
 * gate is the cheapest single paid attempt (resolveOperationCost), and the
 * total is shown as an estimate only.
 *
 * ## Why a partial run is confirmed rather than blocked
 *
 * That reasoning holds for whether to BLOCK, but it was also being used to say
 * nothing at all: 20 credits against 25 contacts passed the gate silently, the
 * batch spent the balance around the twentieth, and the remaining five came
 * back indistinguishable from "no data found". The run was fine; not being
 * told was the bug.
 *
 * So the balance is now compared against the whole job as well, and a run it
 * can't see through asks first (see CreditCheckOptions.confirmPartialRun).
 * Confirm, not block — the partial run is still a legitimate thing to want,
 * and the estimate is too soft to refuse on.
 */
export function CreditGuardProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  // Reactive balance, shared with the top-bar badge's own subscription.
  const { data: account, isLoading: isBalanceLoading } = useGetCreditBalanceQuery(
    undefined,
    { skip: !isAuthenticated }
  );
  const balance = account?.runningBalance ?? 0;

  // Everything else is on demand — see the note above.
  const [fetchBalance] = creditApi.useLazyGetCreditBalanceQuery();
  const [fetchProviders] = providersApi.useLazyGetProvidersQuery();
  const [fetchPricing] = pricingApi.useLazyGetPricingBundleQuery();
  const [fetchMyTeam] = teamApi.useLazyGetMyTeamQuery();

  const [isChecking, setIsChecking] = useState(false);
  const [modalContext, setModalContext] = useState<InsufficientCreditsContext | null>(
    null
  );
  const [isTeamMember, setIsTeamMember] = useState(false);
  // The top-up dialog this hands over to, and the published rate it prices off.
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [billingSettings, setBillingSettings] = useState<BillingSettings | null>(
    null
  );

  /**
   * Last resolved prices, kept so the synchronous `hasEnoughCredits` and
   * `getOperationCost` have something real to answer with between checks. A ref
   * rather than state: reading it must not make every consumer re-render, and
   * the value only ever informs a hint (the modal's own figures come from the
   * check that opened it).
   */
  const pricesRef = useRef<PriceData>({
    providersByCategory: {},
    serviceConfigs: undefined,
  });

  // Fetch team membership on mount so pre-emptive buttons can check it
  // immediately without waiting for async checks in individual cells.
  useEffect(() => {
    if (!isAuthenticated) return;

    const initTeamMembership = async () => {
      try {
        const team = await fetchMyTeam(undefined, true).unwrap();
        const isTeam = !!team && !team.canManage;
        setIsTeamMember(isTeam);
      } catch {
        setIsTeamMember(false);
      }
    };

    initTeamMembership();
  }, [isAuthenticated, fetchMyTeam]);

  /**
   * Last known balance, for the fallback below to read WITHOUT depending on it.
   *
   * `balance` was in refreshBalance's dependency array, so every balance change
   * gave refreshBalance a new identity -> checkCredits -> usePeopleActions'
   * revealField -> the people table's `columns` useMemo. TanStack rebuilds its
   * column defs from that, which unmounts and remounts EVERY cell in the table
   * on every balance change and every SSE event. Cheap to miss, expensive to
   * keep: it destroyed cell-local state (the provider modal closed itself the
   * instant anything touched credits) and re-rendered the whole grid constantly.
   */
  const balanceRef = useRef(balance);
  useEffect(() => {
    balanceRef.current = balance;
  }, [balance]);

  const refreshBalance = useCallback(async (): Promise<number> => {
    if (!isAuthenticated) return 0;
    try {
      const fresh = await fetchBalance(undefined).unwrap();
      return fresh?.runningBalance ?? 0;
    } catch {
      // The balance endpoint is down or the session just went. Fall back to the
      // last known figure rather than reporting 0, which would block an action
      // the user may well be able to afford.
      return balanceRef.current;
    }
  }, [isAuthenticated, fetchBalance]);

  /**
   * Prices for one operation. Served from cache when it's there
   * (`preferCacheValue`), so only the first guarded click of a session pays for
   * this.
   */
  const resolvePrices = useCallback(
    async (categories: ProviderCategory[], needsServiceConfigs: boolean) => {
      const providerResults = await Promise.all(
        categories.map(async (category) => {
          try {
            const providers = await fetchProviders(
              { category, active: true },
              true
            ).unwrap();
            return [category, providers] as const;
          } catch {
            return [category, undefined] as const;
          }
        })
      );

      let serviceConfigs = pricesRef.current.serviceConfigs;
      if (needsServiceConfigs) {
        try {
          const bundle = await fetchPricing(undefined, true).unwrap();
          serviceConfigs = bundle?.serviceConfigs;
        } catch {
          // Leave whatever was cached; resolveOperationCost falls back.
        }
      }

      pricesRef.current = {
        providersByCategory: {
          ...pricesRef.current.providersByCategory,
          // Only the categories that actually came back — a failed fetch must
          // not overwrite a good cached catalogue with undefined, which would
          // silently drop that category's price back to `fallbackCost`.
          ...Object.fromEntries(
            providerResults.filter(([, providers]) => providers !== undefined)
          ),
        },
        serviceConfigs,
      };
      return pricesRef.current;
    },
    [fetchProviders, fetchPricing]
  );

  const getOperationCost = useCallback(
    (operation: CreditOperation) =>
      resolveOperationCost(operation, pricesRef.current),
    []
  );

  const hasEnoughCredits = useCallback(
    (operation: CreditOperation) =>
      // Same bar as checkCredits: one item's cost, not the whole job's.
      balance >= resolveOperationCost(operation, pricesRef.current),
    [balance]
  );

  /**
   * Resolver for a partial-run prompt that `checkCredits` is awaiting.
   *
   * Held in a ref rather than in the modal context so that EVERY way out of
   * that dialog settles it — the confirm button, Cancel, Esc, the overlay, and
   * the hand-off to the top-up dialog. A dismissal that left this pending
   * would hang the caller's `await` forever, which for a bulk unlock means a
   * click that silently does nothing.
   */
  const partialResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const settlePartialRun = useCallback((proceed: boolean) => {
    const resolve = partialResolverRef.current;
    partialResolverRef.current = null;
    setModalContext(null);
    resolve?.(proceed);
  }, []);

  const closeModal = useCallback(() => {
    // A partial-run prompt is a question, so dismissing it means "no". A plain
    // insufficient-credits modal has nothing to settle.
    if (partialResolverRef.current) {
      settlePartialRun(false);
      return;
    }
    setModalContext(null);
  }, [settlePartialRun]);

  /**
   * Hands over to the top-up dialog in place.
   *
   * This is a standalone component, so the person
   * who ran out of credits halfway through a search tops up without losing the
   * page — no navigation, no re-running the search afterwards.
   *
   * The fallback still navigates: without the published rate there is no
   * slider to show, and Billing can at least explain itself.
   *
   * Team members (non-admin pool members) can open this dialog. The blocking
   * happens at the CreditGuardModal level (when insufficient credits): they
   * get "OK" button only, no "Buy More Credits". Pre-emptive "Add Credit"
   * buttons are available to everyone, but the insufficient-credits modal
   * stops team members from proceeding.
   */
  const openBuyCredits = useCallback(async () => {
    // Topping up instead of answering the prompt means the run doesn't start:
    // they came here to cover the whole job, not to take the short one.
    if (partialResolverRef.current) settlePartialRun(false);
    setModalContext(null);
    try {
      const bundle = await fetchPricing(undefined, true).unwrap();
      if (!bundle?.billingSettings) throw new Error("no billing settings");
      setBillingSettings(bundle.billingSettings);
      setIsTopUpOpen(true);
    } catch {
      router.push("/settings/billing");
    }
  }, [fetchPricing, router, settlePartialRun]);

  const checkCredits = useCallback(
    async (options: CreditCheckOptions): Promise<CreditCheckResult> => {
      const {
        operation,
        count = 1,
        costOverride,
        confirmPartialRun,
        itemNoun,
        onInsufficientCredits = "modal",
        onInsufficientCallback,
        customMessage,
      } = options;

      setIsChecking(true);
      try {
        const sources = getOperationPriceSources(operation);
        const [prices, availableCredits] = await Promise.all([
          costOverride === undefined
            ? resolvePrices(sources.categories, sources.needsServiceConfigs)
            : Promise.resolve(pricesRef.current),
          refreshBalance(),
        ]);

        const requiredCredits =
          costOverride ?? resolveOperationCost(operation, prices);
        // What each item SPENDS, which for a combined contact unlock is both
        // waterfalls, not the cheaper of the two — see resolveOperationSpend.
        const perItemSpend = costOverride ?? resolveOperationSpend(operation, prices);
        const items = Math.max(count, 1);
        const fullCost = perItemSpend * items;
        const canStart = availableCredits >= requiredCredits;
        // Still optimistic even at the spend price — a contact whose waterfall
        // has to try several providers costs more than its category minimum.
        // Priced at 0 (csv-export today) nothing limits the count.
        const affordableCount =
          perItemSpend > 0
            ? Math.min(items, Math.floor(availableCredits / perItemSpend))
            : items;
        const isPartiallyAffordable = canStart && affordableCount < items;

        const result: CreditCheckResult = {
          canProceed: canStart,
          availableCredits,
          requiredCredits,
          fullCost,
          shortfall: canStart ? 0 : requiredCredits - availableCredits,
          affordableCount,
          isPartiallyAffordable,
        };

        if (canStart) {
          // The balance covers the job: nothing to warn about.
          if (!isPartiallyAffordable) return result;
          // Asked by default for a multi-item run, because the alternative is
          // what this guard exists to prevent — a run that starts, spends the
          // balance partway through, and leaves the rest looking like they
          // simply found nothing. A single item can't run short of itself.
          const shouldConfirm = confirmPartialRun ?? items > 1;
          if (!shouldConfirm || onInsufficientCredits === "silent") return result;

          // Nothing is blocked here, so the caller's spinner shouldn't be left
          // spinning for however long the dialog stays open.
          setIsChecking(false);
          const proceed = await new Promise<boolean>((resolve) => {
            partialResolverRef.current = resolve;
            setModalContext({
              operation,
              requiredCredits,
              availableCredits,
              fullCost,
              count: items,
              customMessage,
              itemNoun,
              partialRun: { affordableCount, onConfirm: settlePartialRun },
            });
          });
          return { ...result, canProceed: proceed };
        }

        switch (onInsufficientCredits) {
          case "modal": {
            // Only asked for once a check has actually failed, and only to get
            // the wording right: a team member is told to ask their admin
            // rather than offered a top-up they cannot make.
            try {
              const team = await fetchMyTeam(undefined, true).unwrap();
              const isTeam = !!team && !team.canManage;
              setIsTeamMember(isTeam);
            } catch {
              setIsTeamMember(false);
            }
            setModalContext({
              operation,
              requiredCredits,
              availableCredits,
              fullCost,
              count: items,
              customMessage,
              itemNoun,
            });
            break;
          }
          case "toast":
            toast.error(
              customMessage ??
                `Not enough credits — you need ${formatCredits(
                  requiredCredits
                )} and have ${formatCredits(availableCredits)}.`,
              {
                id: `credit-guard-${operation}`,
                action: { label: "Buy credits", onClick: openBuyCredits },
              }
            );
            break;
          case "redirect":
            openBuyCredits();
            break;
          case "callback":
            onInsufficientCallback?.(requiredCredits, availableCredits);
            break;
          case "silent":
            break;
        }

        return result;
      } finally {
        setIsChecking(false);
      }
    },
    [resolvePrices, refreshBalance, fetchMyTeam, openBuyCredits, settlePartialRun]
  );

  const value = useMemo<CreditGuardContextValue>(
    () => ({
      balance,
      isLoading: isBalanceLoading || isChecking,
      checkCredits,
      hasEnoughCredits,
      getOperationCost,
      refreshBalance,
      openBuyCredits,
      closeModal,
      isTeamMember,
    }),
    [
      balance,
      isBalanceLoading,
      isChecking,
      checkCredits,
      hasEnoughCredits,
      getOperationCost,
      refreshBalance,
      openBuyCredits,
      closeModal,
      isTeamMember,
    ]
  );

  return (
    <CreditGuardContext.Provider value={value}>
      {children}
      {modalContext ? (
        <CreditGuardModal
          open
          onClose={closeModal}
          context={modalContext}
          isTeamMember={isTeamMember}
          onBuyCredits={openBuyCredits}
        />
      ) : null}

      {/* Mounted only while open, so its own balance query stays idle the rest
          of the time. `billingSettings` is guaranteed non-null here —
          openBuyCredits navigates away instead when it can't get the rate. */}
      {isTopUpOpen ? (
        <BuyCreditsDialog
          billingSettings={billingSettings}
          open={isTopUpOpen}
          onOpenChange={setIsTopUpOpen}
        />
      ) : null}
    </CreditGuardContext.Provider>
  );
}

export function useCreditGuardContext(): CreditGuardContextValue {
  const context = useContext(CreditGuardContext);
  if (!context) {
    throw new Error(
      "useCreditGuardContext must be used inside <CreditGuardProvider>"
    );
  }
  return context;
}

export default CreditGuardProvider;
